import { useEffect, useRef, useState } from 'react';

import { isDev } from '@tankef/env';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos entre checks exitosos
const FETCH_TIMEOUT_MS = 8_000; // 8s — si el servidor no responde, abortamos
const MAX_CONSECUTIVE_FAILURES = 3; // tras 3 fallos seguidos, backoff a 30 min
const BACKOFF_INTERVAL_MS = 30 * 60 * 1000;

// Versión capturada al cargar la app por primera vez.
// Nunca cambia durante la sesión — es la línea base de comparación.
let initialVersion: string | null = null;

async function fetchVersion(): Promise<string | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    });

    if (!res.ok) return null;

    // Validar que la respuesta sea JSON válido con el campo esperado
    const text = await res.text();
    const data = JSON.parse(text) as unknown;
    if (
      typeof data === 'object' &&
      data !== null &&
      'version' in data &&
      typeof (data as Record<string, unknown>).version === 'string'
    ) {
      return (data as { version: string }).version;
    }

    return null;
  } catch {
    // AbortError (timeout), NetworkError, SyntaxError (JSON inválido) — todo silenciado
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Detecta cuando hay una nueva versión del FE disponible en el servidor.
 *
 * Resiliente a:
 *   - Sin conexión / servidor caído → silencioso, reintenta en el siguiente ciclo
 *   - Timeout de red (> 8s) → abort, no bloquea
 *   - Respuesta no-JSON o campo ausente → null, ignora
 *   - 3 fallos consecutivos → backoff a 30 min para no saturar
 *   - version.json inexistente (dev/staging sin build) → nunca activa el banner
 *
 * En dev siempre retorna `hasUpdate: false`.
 */
export function useVersionCheck(): { hasUpdate: boolean; reload: () => void } {
  const [hasUpdate, setHasUpdate] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const failuresRef = useRef(0);

  useEffect(() => {
    if (isDev) return;

    const scheduleNext = (delay: number) => {
      timerRef.current = setTimeout(tick, delay);
    };

    const tick = async () => {
      const version = await fetchVersion();

      if (!version) {
        // Fallo de red, timeout, JSON inválido, 404, etc.
        failuresRef.current += 1;
        const delay =
          failuresRef.current >= MAX_CONSECUTIVE_FAILURES ? BACKOFF_INTERVAL_MS : POLL_INTERVAL_MS;
        scheduleNext(delay);
        return;
      }

      // Fetch exitoso — resetear contador de fallos
      failuresRef.current = 0;

      if (initialVersion === null) {
        initialVersion = version;
        scheduleNext(POLL_INTERVAL_MS);
        return;
      }

      if (version !== initialVersion) {
        setHasUpdate(true);
        // Ya no necesitamos seguir haciendo polling
        return;
      }

      scheduleNext(POLL_INTERVAL_MS);
    };

    tick();

    return () => clearTimeout(timerRef.current);
  }, []);

  return { hasUpdate, reload: () => window.location.reload() };
}
